import type { AppDispatch, AppState } from 'app/store';
import { delay } from 'utils';

/* eslint-disable no-unused-vars */
type FetchThunk<T = unknown> = (
  dispatch: AppDispatch,
  getState: AppState,
) => Promise<T>;
/* eslint-enable no-unused-vars */

type DelayedFetchResult<F> = F extends FetchThunk[]
  ? PromiseSettledResult<unknown>[]
  : F extends FetchThunk<infer T>
    ? T
    : never;

/* Gives the BE time to persist a write before the follow-up read. */
const DEFAULT_FETCH_DELAY_MS = 2000;

/**
 * Waits `ms`, then dispatches the fetch(es).
 * One thunk resolves/rejects with that fetch; an array runs in parallel with
 * `allSettled`, so a failed refresh never fails the caller.
 */
function fetchAfterDelay<F extends FetchThunk | FetchThunk[]>(
  fetch: F,
  ms = DEFAULT_FETCH_DELAY_MS,
) {
  return async (dispatch: AppDispatch) => {
    await delay(ms);
    const result = Array.isArray(fetch)
      ? Promise.allSettled(fetch.map((fetchThunk) => dispatch(fetchThunk)))
      : dispatch(fetch as FetchThunk);
    return result as Promise<DelayedFetchResult<F>>;
  };
}

export default fetchAfterDelay;
